/**
 * Finding the passages a correction points at, and putting the notes at them.
 *
 * A point of a judgement names its passage as a **quote** and never as a position — see
 * `core/exam/Assessment.kt` for why. So the passage has to be looked up, and this is where that
 * happens: in the *drawn* answer rather than in its source, because the drawn answer is what
 * somebody is reading and because a quote out of the text never carries the markup around it.
 *
 * Two things make the lookup forgiving in exactly one respect and strict in every other:
 *
 * - **Whitespace does not count.** A renderer turns a line break inside a paragraph into a space,
 *   and a quote copied out of the source carries whatever was there. Runs of whitespace are
 *   therefore one space on both sides of the comparison.
 * - **Nothing else is forgiven.** No case folding, no punctuation smoothing, no nearest match. A
 *   passage that is not found is reported as not found, and the point is read in the judgement with
 *   a line saying so — which is honest, where a nearest match would put somebody's correction at a
 *   sentence it was never about.
 *
 * A passage is looked for **inside one block**: a paragraph, a list item, a cell, a heading. A quote
 * that ran from the end of one paragraph into the next is not a passage anybody wrote a point about.
 *
 * ## Overlapping passages
 *
 * Two points may be about passages that overlap — one inside the other, half in and half out, or
 * twice the same sentence. HTML cannot carry crossing elements, so marking one and then the other is
 * not merely awkward, it is impossible: the second would have to start inside the first and end
 * outside it. Every passage of a block is therefore found **first**, and the found ranges are then
 * cut into **disjoint segments**, each knowing every point it belongs to. A segment owned by two
 * points says so — it carries both numbers and is drawn as the overlap it is.
 *
 * Before this, the second passage was simply reported as not found, which was a false statement
 * about somebody's own text.
 *
 * ## Which note belongs to which passage
 *
 * The note stands at the **end of the paragraph** its passage is in, not immediately behind the
 * passage: the paragraph somebody wrote stays one paragraph. What ties the two together is a
 * **number** — on the mark and on the note — counted in the reading order of the *text*, never in
 * the order the assistant happened to write its points in. The number is a property of the answer.
 */

/** One passage to look for: which point it belongs to, and the words themselves. */
export interface Passage {
  readonly index: number
  readonly quote: string
}

/** What a note says, asked for while the text is being marked. */
export interface Note {
  readonly tone: string
  readonly text: string
}

/** What came of marking: which number each point got, and which passages were not there. */
export interface Marked {
  /** The point's index in the judgement, against the number shown at its passage. */
  readonly numbers: ReadonlyMap<number, number>
  readonly unfound: readonly number[]
}

const BLOCKS = new Set([
  'P',
  'LI',
  'TD',
  'TH',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'BLOCKQUOTE',
  'PRE',
  'DT',
  'DD',
  'FIGCAPTION',
])

/** The class every marked passage carries. Which points it belongs to are data attributes on it. */
export const MARK_CLASS = 'answer-mark'

/** The class every note carries, wherever it is put. */
export const NOTE_CLASS = 'answer-note'

/** Whitespace flattened to single spaces, and the edges cut. What both sides are compared as. */
export function flattened(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

interface Spot {
  readonly node: Text
  readonly offset: number
}

/** One passage, found: where it starts and ends in the flattened text of its block. */
interface Found {
  readonly index: number
  readonly from: number
  readonly to: number
}

/** The block a text node belongs to, or `root` where nothing else claims it. */
function blockOf(node: Text, root: HTMLElement): HTMLElement {
  let at: HTMLElement | null = node.parentElement
  while (at !== null && at !== root) {
    if (BLOCKS.has(at.tagName)) {
      return at
    }
    at = at.parentElement
  }
  return root
}

/** Every text node under [root], in reading order. */
function textNodesIn(root: HTMLElement): Text[] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const found: Text[] = []
  let node = walker.nextNode()
  while (node !== null) {
    found.push(node as Text)
    node = walker.nextNode()
  }
  return found
}

/**
 * The text of one block as it is compared, with a spot kept for every character of it.
 *
 * The spot is where that character sits in the DOM, which is what lets a match be turned back into a
 * range over the real nodes rather than over a string nobody can see.
 */
function flattenedWithSpots(nodes: readonly Text[]): { text: string; spots: Spot[] } {
  let text = ''
  const spots: Spot[] = []
  let lastWasSpace = true
  for (const node of nodes) {
    const raw = node.data
    for (let offset = 0; offset < raw.length; offset += 1) {
      const character = raw[offset] ?? ''
      if (/\s/.test(character)) {
        if (!lastWasSpace) {
          text += ' '
          spots.push({ node, offset })
          lastWasSpace = true
        }
        continue
      }
      text += character
      spots.push({ node, offset })
      lastWasSpace = false
    }
  }
  // A trailing space belongs to nothing.
  if (text.endsWith(' ')) {
    text = text.slice(0, -1)
    spots.pop()
  }
  return { text, spots }
}

/**
 * The found ranges of one block, cut into stretches that do not overlap.
 *
 * Every start and every end is a boundary; between two neighbouring boundaries the set of points
 * covering the text does not change, so that stretch is one segment. A stretch nobody covers is
 * dropped.
 */
function segmentsOf(found: readonly Found[]): { from: number; to: number; of: number[] }[] {
  const boundaries = [...new Set(found.flatMap((one) => [one.from, one.to]))].sort((a, b) => a - b)
  const segments: { from: number; to: number; of: number[] }[] = []
  for (let at = 0; at < boundaries.length - 1; at += 1) {
    const from = boundaries[at] ?? 0
    const to = boundaries[at + 1] ?? 0
    const of = found.filter((one) => one.from <= from && one.to >= to).map((one) => one.index)
    if (of.length > 0) {
      segments.push({ from, to, of })
    }
  }
  return segments
}

/** Wraps the characters from [from] to [to] of a block in a mark belonging to [of]. */
function wrap(
  spots: readonly Spot[],
  from: number,
  to: number,
  of: readonly number[],
  numbers: ReadonlyMap<number, number>,
  toneOf: (index: number) => string | undefined,
): void {
  const start = spots[from]
  const end = spots[to - 1]
  if (start === undefined || end === undefined) {
    return
  }
  const range = document.createRange()
  range.setStart(start.node, start.offset)
  range.setEnd(end.node, end.offset + 1)

  const mark = document.createElement('mark')
  mark.className = MARK_CLASS
  mark.dataset.notes = of.join(',')
  // One point: its tone colours the passage. Several: no tone at all, because two washes laid over
  // each other are a third colour that means nothing. The overlap is drawn as an overlap instead.
  const tone = of.length === 1 ? toneOf(of[0] ?? -1) : undefined
  if (tone !== undefined) {
    mark.dataset.tone = tone
  }
  if (of.length > 1) {
    mark.dataset.overlapping = 'true'
  }
  // `surroundContents` refuses a range that crosses a node boundary, which is the common case here —
  // a sentence with an emphasised word in it. Taking the contents out and putting them back inside
  // the mark does cross it, and the range is known to stay inside one block.
  mark.appendChild(range.extractContents())

  const said = of.map((one) => numbers.get(one)).filter((one) => one !== undefined)
  if (said.length > 0) {
    const shown = document.createElement('sup')
    shown.className = `${MARK_CLASS}__n`
    shown.textContent = said.join(',')
    mark.appendChild(shown)
  }

  range.insertNode(mark)
}

/** The note itself, as it is put into the text. */
function noteFor(index: number, number: number, note: Note): HTMLElement {
  const aside = document.createElement('aside')
  aside.className = NOTE_CLASS
  aside.dataset.note = String(index)
  aside.dataset.tone = note.tone.toLowerCase()

  const said = document.createElement('span')
  said.className = `${NOTE_CLASS}__n`
  said.textContent = String(number)
  aside.appendChild(said)

  const text = document.createElement('span')
  text.className = `${NOTE_CLASS}__text`
  text.textContent = note.text
  aside.appendChild(text)

  return aside
}

/**
 * Marks every passage of [passages] inside [root] and puts each point's note at its paragraph.
 *
 * The segments of one block are wrapped **from the back**, so that the spots of the earlier ones
 * still hold: splitting a text node at a later offset leaves everything before it in the very same
 * node.
 */
export function markPassages(
  root: HTMLElement,
  passages: readonly Passage[],
  noteOf: (index: number) => Note | undefined = () => undefined,
): Marked {
  const byBlock = new Map<HTMLElement, Text[]>()
  for (const node of textNodesIn(root)) {
    const block = blockOf(node, root)
    const held = byBlock.get(block)
    if (held === undefined) {
      byBlock.set(block, [node])
    } else {
      held.push(node)
    }
  }

  // Found first, every one of them, before a single node is touched. Wrapping as we go is what made
  // an overlapping passage look like a missing one.
  const placed = new Map<HTMLElement, { spots: Spot[]; found: Found[] }>()
  const stillLooking = new Set(passages.map((one) => one.index))
  for (const [block, nodes] of byBlock) {
    const { text, spots } = flattenedWithSpots(nodes)
    const found: Found[] = []
    for (const passage of passages) {
      if (!stillLooking.has(passage.index)) {
        continue
      }
      const needle = flattened(passage.quote)
      if (needle === '') {
        continue
      }
      const at = text.indexOf(needle)
      if (at < 0) {
        continue
      }
      found.push({ index: passage.index, from: at, to: at + needle.length })
      stillLooking.delete(passage.index)
    }
    if (found.length > 0) {
      placed.set(block, { spots, found })
    }
  }

  // The numbers, in the reading order of the text: blocks in document order, and inside a block by
  // where the passage begins.
  const numbers = new Map<number, number>()
  for (const { found } of placed.values()) {
    for (const one of [...found].sort((a, b) => a.from - b.from || a.to - b.to)) {
      numbers.set(one.index, numbers.size + 1)
    }
  }

  const toneOf = (index: number) => noteOf(index)?.tone.toLowerCase()

  for (const [block, { spots, found }] of placed) {
    for (const segment of segmentsOf(found).sort((a, b) => b.from - a.from)) {
      wrap(spots, segment.from, segment.to, segment.of, numbers, toneOf)
    }

    // The notes, at the end of this paragraph, in the order their passages begin in it. Put after a
    // moving cursor rather than after the block every time, or each one would push the one before it
    // further down and the order would come out backwards.
    let after: Element = block
    for (const one of [...found].sort((a, b) => a.from - b.from || a.to - b.to)) {
      const note = noteOf(one.index)
      const number = numbers.get(one.index)
      if (note === undefined || number === undefined) {
        continue
      }
      const element = noteFor(one.index, number, note)
      if (after === root) {
        // Text that sits in no block of its own — the note goes at the end of everything rather
        // than outside the drawn answer.
        root.appendChild(element)
      } else {
        after.insertAdjacentElement('afterend', element)
      }
      after = element
    }
  }

  return { numbers, unfound: [...stillLooking] }
}

/**
 * Where a scroll port has to stand for [box] to be in the middle of it.
 *
 * Pulled out as arithmetic of its own because that is the half worth testing: jsdom lays nothing
 * out, so a test can hand this the rectangles a browser would have measured and check the answer.
 * Never below zero, and never past the end.
 */
export function scrolledTo(
  at: number,
  port: { top: number; height: number; scrollHeight: number },
  box: { top: number; height: number },
): number {
  const wanted = at + (box.top - port.top) - (port.height - box.height) / 2
  return Math.max(0, Math.min(wanted, Math.max(0, port.scrollHeight - port.height)))
}

/** The nearest ancestor of [element] that actually scrolls, or `null` if the page is the one. */
export function scrollPortOf(element: HTMLElement): HTMLElement | null {
  let at = element.parentElement
  while (at !== null) {
    const how = getComputedStyle(at).overflowY
    if (
      (how === 'auto' || how === 'scroll' || how === 'overlay') &&
      at.scrollHeight > at.clientHeight
    ) {
      return at
    }
    at = at.parentElement
  }
  return null
}

/**
 * Brings [element] into the middle of whatever scrolls around it.
 *
 * `scrollIntoView` was what this did, and it did not work: the answer sits in nested boxes that each
 * declare `overflow-y: auto` while only one of them has anything to scroll, and the browser's own
 * walk up that chain did not land where the passage was. Finding the port that genuinely scrolls and
 * setting `scrollTop` outright is one line longer and says what it means.
 */
export function bringIntoView(element: HTMLElement): void {
  const port = scrollPortOf(element)
  if (port === null) {
    element.scrollIntoView?.({ block: 'center' })
    return
  }
  const portBox = port.getBoundingClientRect()
  const box = element.getBoundingClientRect()
  port.scrollTop = scrolledTo(
    port.scrollTop,
    { top: portBox.top, height: port.clientHeight, scrollHeight: port.scrollHeight },
    { top: box.top, height: box.height },
  )
}
